import { useEffect, useRef } from 'react'
import { useTexte } from '../../texte'

import { BergIcon, GifIcon, PhotoIcon, PollIcon, SonneIcon } from '../../components/Icons'

/**
 * Was an eine Nachricht kann — hinter **einem** Knopf.
 *
 * Aus dem Betrieb: „am Smartphone ist das Eingabefeld zu schmal: die Symbole
 * für Fotoupload und GIF enger zusammen oder untereinander oder durch ein +
 * ersetzen, und dann ein Auswahlfenster mit Foto/Video / Kamera / GIF."
 *
 * **Die Kamera ist wieder heraus**, und zwar auf Ansage: „die Kamera aus dem
 * Auswahlmenü herausnehmen (nur Foto/Video + GIF)." Sie brachte nichts, was
 * die Zeile darüber nicht auch kann -- die Foto-App eines Telefons hat einen
 * Auslöser -- und sie kam mit eigenen Sonderfällen: `capture="environment"`
 * ist der Weg, über den die gemeldeten 360p ins Haus kamen, und ein zweites
 * Dateifeld ist ein zweiter Weg zu derselben Sache.
 *
 * **Der Platz ist der Punkt.** Jeder Knopf in der Eingabezeile ist 44 Punkte
 * breit, weil er es sein muss; auf 390 Punkten bleibt für den Text, was die
 * Knöpfe übrig lassen. Zwei davon in einen zu legen gibt dem Feld 52 Punkte
 * zurück.
 *
 * **Es ist eine Liste und keine Reihe.** Eine Reihe aus 44-Punkt-Flächen ist
 * nach acht Stück breiter als ein iPhone; untereinander kann sie nicht
 * hinausragen, und jede Zeile trägt ihren Namen statt eines Symbols, das man
 * erst lernen müsste.
 *
 * **Das Blatt löst nichts selbst aus.** Es meldet, was gewählt wurde; die
 * Dateifelder liegen in der Eingabezeile. Das ist keine Förmlichkeit: auf iOS
 * öffnet ein Dateifeld nur, wenn der Griff aus einer echten Geste kommt, und
 * ein Tipp auf eine Zeile ist genau das — ein Umweg über einen Zustand wäre es
 * nicht mehr.
 */
export function Anhangwahl({
  darfDateien,
  nurFotos = false,
  gifs,
  abstimmung,
  wetter,
  wetterAnderswo = false,
  tag,
  onFotoOderVideo,
  onGif,
  onAbstimmung,
  onWetter,
  onWetterAnderswo,
  onTag,
  onSchliessen,
}: {
  /** Ob die Zeile für Dateien überhaupt dasteht. */
  darfDateien: boolean
  /**
   * Nur Fotos, kein Video — der Fall des Mitlesenden (#59).
   *
   * Die Zeile sagt, was geht, statt etwas anzubieten und es danach
   * abzulehnen. Genau das war der Fehler, den es hier schon einmal gab.
   */
  nurFotos?: boolean
  /** Ohne Schlüssel gibt es keine GIF-Suche und deshalb auch keine Zeile. */
  gifs: boolean
  /**
   * Eine Abstimmung anzulegen ist ein Beitrag an alle — das darf nur der
   * Reisende, und nur im Reise-Chat. Im Verlauf mit einem Einzelnen wäre sie
   * eine Abstimmung mit einem Menschen.
   */
  abstimmung: boolean
  /** The current weather as a card (0.31.0) -- only for the traveller, only with access to Apple. */
  wetter: boolean
  /** The weather at a chosen place and hour (#83) -- needs the place search too, so Apple Maps as well. */
  wetterAnderswo?: boolean
  /** The day card (#79): the traveller only, and only where a route is. */
  tag: boolean
  onFotoOderVideo: () => void
  onGif: () => void
  onAbstimmung: () => void
  onWetter: () => void
  onWetterAnderswo?: () => void
  onTag: () => void
  onSchliessen: () => void
}) {
  const kasten = useRef<HTMLDivElement>(null)

  const t = useTexte()
  useEffect(() => {
    function beiTaste(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onSchliessen()
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [onSchliessen])

  // **Ein Tipp daneben schließt.** Ohne das bliebe das Blatt stehen, bis
  // jemand es ausdrücklich zumacht -- und es deckt die halbe Eingabezeile.
  useEffect(() => {
    function beiTipp(event: PointerEvent) {
      const ziel = event.target
      if (ziel instanceof Node && kasten.current?.contains(ziel) === true) return
      onSchliessen()
    }
    // In der Aufnahmephase: ein Knopf darunter darf seine Sache trotzdem tun,
    // aber das Blatt schliesst dabei.
    window.addEventListener('pointerdown', beiTipp)
    return () => window.removeEventListener('pointerdown', beiTipp)
  }, [onSchliessen])

  return (
    <div
      ref={kasten}
      className="anhangwahl"
      role="dialog"
      aria-modal="true"
      aria-label={t.chat.wasAnhaengen}
    >
      {darfDateien && (
        <button type="button" className="anhangwahl__zeile" onClick={onFotoOderVideo}>
          <PhotoIcon size={20} />
          <span>{nurFotos ? t.chat.nurFoto : t.chat.fotoOderVideo}</span>
        </button>
      )}
      {gifs && (
        <button type="button" className="anhangwahl__zeile" onClick={onGif}>
          <GifIcon size={20} />
          {/* „GIF suchen" und nicht „GIF": das Sinnbild trägt das Wort schon,
              und zweimal dasselbe nebeneinander liest sich als Fehler. Der
              Zusatz sagt außerdem, was passiert -- es öffnet eine Suche. */}
          <span>GIF suchen</span>
        </button>
      )}
      {abstimmung && (
        <button type="button" className="anhangwahl__zeile" onClick={onAbstimmung}>
          <PollIcon size={20} />
          <span>{t.chat.abstimmung}</span>
        </button>
      )}
      {wetter && (
        <button type="button" className="anhangwahl__zeile" data-anhang="wetter" onClick={onWetter}>
          <SonneIcon size={20} />
          <span>{t.chat.wetter}</span>
        </button>
      )}
      {wetter && wetterAnderswo && (
        <button type="button" className="anhangwahl__zeile" data-anhang="wetter-anderswo" onClick={onWetterAnderswo}>
          <SonneIcon size={20} />
          <span>{t.chat.wetterAnderswo}</span>
        </button>
      )}
      {tag && (
        <button type="button" className="anhangwahl__zeile" data-anhang="tag" onClick={onTag}>
          <BergIcon size={20} />
          <span>{t.chat.streckenstatistik}</span>
        </button>
      )}
    </div>
  )
}

/**
 * Wie viele Wege es gibt — und damit, ob sich ein Sammelknopf lohnt.
 *
 * **Bei einem einzigen Weg gibt es keinen `+`.** Ein Knopf, der ein Blatt mit
 * genau einer Zeile öffnet, ist ein Griff mehr für nichts; dann steht der eine
 * Knopf wieder direkt da. Das betraf die Mitlesenden, solange sie nur GIFs
 * schicken durften; seit #59 haben auch sie zwei Wege, und der `+` steht damit
 * bei ihnen ebenfalls.
 */
export function wegeZaehlen(darfDateien: boolean, gifs: boolean, abstimmung: boolean, wetter = false, tag = false, wetterAnderswo = false): number {
  return (darfDateien ? 1 : 0) + (gifs ? 1 : 0) + (abstimmung ? 1 : 0) + (wetter ? 1 : 0) + (tag ? 1 : 0) + (wetter && wetterAnderswo ? 1 : 0)
}
